import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import Image from 'next/image';

// Components
import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon';
import Modal from '@/components/base/Modal';

// Lib
import { SUBSCRIPTION_INVOICES } from '@/lib/routes/privateRoutes';
import { CHANGE_YOUR_PLAN } from '@/lib/settings/subscriptionInvoices/steps';

type Benefit = {
    text: string;
    icon: 'check' | 'plus';
};

export const ModalUpsellItem = (props: { benefit: Benefit }) => {
    const { benefit } = props;
    return (
        <div className="flex items-center space-x-2 text-icoBlack">
            <Icon icon={benefit.icon} data-testid={`icon-${benefit.icon}`} />
            <span className="font-GMedium">{benefit.text}</span>
        </div>
    );
};

export const ModalUpsell = (props: {
    heading: string;
    description: string;
    benefits: Benefit[];
    illustrationPath: string;
    dismiss: () => void;
    isOpen?: boolean;
    icon?: 'rocket-outline' | 'gem-outline';
}) => {
    const {
        heading,
        description,
        benefits,
        illustrationPath,
        dismiss,
        isOpen = true,
        icon = 'rocket-outline'
    } = props;
    const { t } = useTranslation('commons');
    return (
        <Modal
            size="5xl"
            open={isOpen}
            onDismiss={dismiss}
            data-testid="modal-upsell"
            className="z-50">
            <Modal.Body className="w-full">
                <Modal.Content className="flex">
                    <div className="hidden w-full rounded-l-md bg-gradient-to-br from-[#E6F3FFB2] to-[#96D8FFE5] pl-8 pt-8 sm:block">
                        <div className="relative size-full">
                            <Image
                                src={illustrationPath}
                                alt=""
                                fill
                                objectFit="cover"
                                objectPosition="left center"
                                className="rounded-tl-md"
                            />
                        </div>
                    </div>
                    <div className="flex flex-col space-y-[30px] p-12">
                        <div className="flex flex-col space-y-5">
                            <div className="flex w-fit items-center space-x-2 rounded-full border border-icoBlue-200 bg-icoBlue-100 px-2 py-1">
                                <Icon icon="crown-outline" className="text-xl text-icoBlue" />
                                <span className="font-GMedium text-xs text-icoBlue">
                                    {t('upsell_modal.hint')}
                                </span>
                            </div>
                            <span className="font-GBold text-3.5xl text-icoBlack">
                                {heading}
                                <Icon icon={icon} className="text-2xl text-icoBlue-400" />
                            </span>
                        </div>
                        <span className="font-GMedium text-gray-500">{description}</span>
                        <div className="space-y-3">
                            {benefits.map((benefit) => (
                                <ModalUpsellItem benefit={benefit} key={`${benefit.text}-i`} />
                            ))}
                        </div>
                        <div className="flex w-full justify-end">
                            <Link
                                className="w-full sm:w-fit"
                                href={{
                                    pathname: SUBSCRIPTION_INVOICES,
                                    query: {
                                        step: CHANGE_YOUR_PLAN
                                    }
                                }}>
                                <Button className="w-full space-x-2">
                                    <span className="w-full">{t('upsell_modal.cta')}</span>
                                    <Icon icon="arrow-right" className="text-xl" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </Modal.Content>
            </Modal.Body>
        </Modal>
    );
};
